// src/components/test/AnswerOptions.tsx
import React from 'react';
import styled from 'styled-components';
import { Button } from '../common/Button';
import { AnswerOption } from '../../types/testData';

const OptionsGrid = styled.div<{ columns?: number }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};

  & > * {
    flex: 0 0 calc(${({ columns = 2 }) => (100 / columns).toFixed(4)}% - ${({ columns = 2 }) => (16 * (columns - 1) / columns).toFixed(2)}px);
    min-width: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: 9px;

    & > * {
      flex: 1 1 100%;
    }
  }
`;

const OptionButton = styled(Button)<{
  $selected?: boolean;
  $disabled?: boolean;
}>`
  /*justify-content: flex-start;*/
  /*text-align: left;*/
  /*justify-content: flex-start;*/
  /*text-align: left;*/
  justify-content: center;
  text-align: center;  
  padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.xl}`};
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border: 2px solid ${({ $selected, theme }) =>
    ($selected ? theme.colors.textLight : theme.colors.border)};
  box-shadow: ${({ $selected, theme }) =>
    ($selected ? `inset 0 0 0 1px ${theme.colors.textLight}` : 'none')};
  color: ${({ $selected, theme }) =>
    ($selected ? theme.colors.text : theme.colors.textLight)};
  /* Жирность не меняется при клике/выборе: шрифт всегда обычный (400).
     Выделение кнопки — только контуром (border + box-shadow) и лёгким затемнением фона. */
  font-weight: 400;
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease, transform 0.2s ease;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  animation: none; /* отключаем fadeIn от базового Button: кнопки не «всплывают» при смене стимула */

  /* Левый цветной маркер не нужен: выделение — только контуром. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background-color: transparent;
  }

  /* Лёгкая заливка при нажатии (и hover на тач-устройствах) — едва темнее белого (#f5f5f5),
     без резкого тёмно-серого от базового Button (outline воспринимает первый тап как :hover,
     что давало бы насыщенный серый). Перекрываем фон И цвет: цвет остаётся тем же, что и в
     обычном состоянии (не «белеет» из-за outline:hover → color: white). */
  &:active:not(:disabled):not([disabled]),
  &:hover:not(:disabled):not([disabled]) {
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ $selected, theme }) =>
      ($selected ? theme.colors.text : theme.colors.textLight)};
  }

  /* Смена границы/цвета по hover — только на устройствах с мышью (hover),
     чтобы на тач-экранах не было цвета от outline-версии базового Button. */
  @media (hover: hover) {
    &:hover:not(:disabled):not([disabled]) {
      background-color: ${({ theme }) => theme.colors.background};
      border-color: ${({ theme }) => theme.colors.textLight};
      color: ${({ theme }) => theme.colors.text};
      transform: translateX(4px);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 6px 10px;
    font-size: 0.85rem;
    line-height: 1.3;
    min-height: 46px;
  }
`;

const OptionText = styled.span`
  flex: 1;
  line-height: 1.5;
`;

const OptionMediaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;

  img {
    max-width: 100%;
    max-height: 120px;
    object-fit: contain;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

interface AnswerOptionsProps {
  options: AnswerOption[];
  /** id выбранного варианта (один клик) или список id кликов в порядке нажатия (мульти-режим). */
  selectedOptionId?: number | number[];
  onOptionSelect: (optionId: number) => void;
  disabled?: boolean;
}

export const AnswerOptions: React.FC<AnswerOptionsProps> = ({
  options,
  selectedOptionId,
  onOptionSelect,
  disabled = false,
}) => {
  // Определяем количество колонок: 2–3 в зависимости от количества вариантов
  const columns = Math.min(3, Math.max(2, Math.ceil(options.length / 3)));
  // Мульти-режим: передан массив уже сделанных кликов
  const pickedIds = Array.isArray(selectedOptionId) ? selectedOptionId : [];
  const isMulti = Array.isArray(selectedOptionId);

  return (
    <OptionsGrid columns={columns}>
    {options.map((option) => {
      const isSelected = isMulti
        ? option.id !== undefined && pickedIds.includes(option.id)
        : selectedOptionId === option.id;
      // В мульти-режиме кликнутая кнопка «утоплена» и больше не активна
      const isLocked = disabled || (isMulti && isSelected);

      return (
        <OptionButton
          key={option.id} // ← здесь key уже безопасен, т.к. в map id есть
          variant="outline"
          fullWidth
          $selected={isSelected}
          $disabled={isLocked}
          onClick={() => {
            if (!isLocked && option.id !== undefined) {
              onOptionSelect(option.id); // ← теперь TS уверен, что id — number
            }
          }}
        >
          {option.media_url ? (
            <OptionMediaContainer>
              <img src={option.media_url} alt={option.content} loading="lazy" />
              {option.content && <OptionText>{option.content}</OptionText>}
            </OptionMediaContainer>
          ) : (
            <OptionText>{option.content}</OptionText>
          )}
        </OptionButton>
      );
    })}
    </OptionsGrid>
  );
};
